import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import "./institutional.css";
import { apiFetch } from "../api/client";
import { getEffectiveRoles, getSimulatedHasMemberRecord, getSimulatedIsMusician, getSimulatedMinistryFlag } from "../roleSimulator";

const NAV_ITEMS = [
  { to: "/", label: "Início", end: true },
  { to: "/sobre", label: "Sobre" },
  { to: "/ministerios", label: "Ministérios" },
  { to: "/pregacoes", label: "Vídeos" },
  { to: "/eventos", label: "Eventos" },
  { to: "/familia", label: "Devocionais" },
  { to: "/contato", label: "Contato" }
];

const TAB_ITEMS = [
  { to: "/", label: "Início", end: true, icon: <path d="M3 11.5 12 4l9 7.5" /> },
  { to: "/sobre", label: "Sobre", icon: <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" /> },
  { to: "/pregacoes", label: "Vídeos", icon: <circle cx="12" cy="12" r="9.2" /> },
  { to: "/eventos", label: "Eventos", icon: <rect x="3.5" y="5" width="17" height="15.5" rx="2.4" /> },
  { to: "/familia", label: "Devocionais", icon: <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H12v18H6.5A2.5 2.5 0 0 1 4 18.5Z" /> }
];

// Iniciais do nome pra avatar (ex.: "Carla Carbonaro" -> "CC"), mesma lógica
// visual do módulo Família Mosaico (profileInitials em PrototypeApp.js).
function getInitials(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function navClass({ isActive }) {
  return "navlink" + (isActive ? " active" : "");
}
function tabClass({ isActive }) {
  return "tabbtn" + (isActive ? " active" : "");
}

export default function InstitutionalLayout() {
  const location = useLocation();
  const mainRef = useRef(null);
  const isFirstRender = useRef(true);
  // Hidratacao otimista a partir do mesmo cache do modulo Familia Mosaico
  // (localStorage "mosaico_user") -- sem isso, viewerUser comeca null em
  // TODA carga de pagina nova (inclusive apos o reload de pagina inteira
  // que /financeiro forca, por ser link real, nao NavLink), entao um
  // ADMIN via o item "Financeiro" sumir e reaparecer no menu a cada
  // navegacao -- o "menu pulando" relatado ao alternar entre Devocionais/
  // Contato/Financeiro. O fetch de /auth/me abaixo continua rodando e
  // corrige o estado (inclusive limpando se a sessao expirou).
  const [viewerUser, setViewerUser] = useState(() => {
    try {
      const raw = window.localStorage.getItem("mosaico_user");
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  });
  // hasMemberRecord = a conta tem cadastro de membresia (Member) vinculado
  // E esse cadastro esta ATIVO (viewerUser.linkedMember.status,
  // selecionado em publicUserSelect/auth.js) -- ter so uma conta de app
  // logada nao basta (pedido: "so pra quem estiver no cadastro de
  // membro"), e um Member vinculado que nao esteja ATIVO tambem nao conta
  // (pedido do Rodrigo, 24/10/2026). Quando um ADMIN de verdade esta
  // simulando "Membro (sem papel especial)", assume que o cadastro ativo
  // existe -- mesmo racional aplicado em PrototypeApp.js.
  const realIsAdminUser = Boolean(viewerUser?.roles?.includes("ADMIN"));
  const effectiveRoles = getEffectiveRoles(viewerUser);
  const hasMemberRecord = getSimulatedHasMemberRecord(
    realIsAdminUser,
    viewerUser?.linkedMember?.status === "ATIVO"
  );
  // O link /financeiro aparece pra ADMIN/FINANCEIRO (sempre) ou pra quem
  // tem cadastro de membresia vinculado -- desde que /financeiro passou a
  // ter um resumo pro membro comum (mes atual, entradas/saidas por
  // categoria, sem grafico/lancamentos, que continuam restritos a
  // ADMIN/FINANCEIRO dentro da propria pagina), o link deixou de ser
  // exclusivo desses dois papeis, mas continua exigindo cadastro de
  // membro -- so ter conta de app logada nao basta.
  const canSeeFinanceiro = Boolean(
    effectiveRoles.includes("ADMIN") || effectiveRoles.includes("FINANCEIRO") || hasMemberRecord
  );
  // Escala de musicos -- link do topo institucional pra quem toca na equipe
  // de louvor, direto pro painel /admin/escalas. Antes esse lugar era do
  // link "Repertório" (adicionado 17/10/2026); trocado por Escala a pedido
  // do Drigo (18/10/2026) -- o repertório continua acessivel por dentro do
  // app Família Mosaico normalmente, só não tem mais link proprio aqui no
  // topo do site.
  //
  // A capacidade de musico de verdade mora na Person (isMusician), nao em
  // User.roles -- corrigido 20/10/2026 depois do Gabriel ficar sem acesso
  // mesmo escalado (um merge de cadastro anterior nunca sincronizou
  // User.roles). effectiveRoles.includes("MUSICO") continua sendo checado
  // como atalho (cobre quem tem o papel certo na conta, sem precisar do
  // dado extra), mas quem nao tem o papel ainda ve o link se a Person
  // vinculada a conta estiver marcada isMusician=true.
  // Generalizado em 28/10/2026 (mesmo mirror de PrototypeApp.js/
  // AdminEscalas.jsx): qualquer outra flag de ministerio na Person tambem
  // abre o link, alem de Musico -- "quem e' marcado como teens ve a
  // escala de teens, de zeladoria ve a escala de zeladoria e assim por
  // diante". So' Musico tem simulador de "Ver como" por enquanto.
  const OUTRAS_FLAGS_DE_ESCALA = [
    "isZelador",
    "isKidsVoluntario",
    "isTeensVoluntario",
    "isMidiaVoluntario",
    "isCafeVoluntario",
    "isRecepcaoVoluntario",
    "isPodcastVoluntario",
    "isPregacaoVoluntario",
    "isTeensDevocionalVoluntario"
  ];
  const canSeeEscala = Boolean(
    effectiveRoles.includes("ADMIN")
    || effectiveRoles.includes("MUSICO")
    || getSimulatedIsMusician(realIsAdminUser, viewerUser?.person?.isMusician)
    || OUTRAS_FLAGS_DE_ESCALA.some((flag) => getSimulatedMinistryFlag(realIsAdminUser, flag, viewerUser?.person?.[flag]))
  );

  // Mesma checagem de sessão do módulo Família Mosaico (/auth/me): se o
  // cookie de sessão (agora primeira-parte, .batistamosaico.com.br) ainda é
  // válido, troca o "Entrar" por "Bem-vindo" + acesso ao perfil -- barra
  // única refletindo o mesmo estado de login nos dois lados do site.
  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((user) => {
        if (alive && user) setViewerUser(user);
      })
      .catch(() => {
        if (alive) setViewerUser(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Move o foco pro heading da página nova ao navegar (não no carregamento
  // inicial), pra leitor de tela anunciar a troca e teclado não ficar
  // "preso" no header — mesma lógica de acessibilidade já validada no
  // protótipo (ver PRD, revisão de acessibilidade de 07/09).
  useEffect(() => {
    // Se a navegação já carrega uma âncora (ex.: /contato#give-card-section,
    // usado pelo botão "Ver formas de contribuir"), não força scroll pro topo --
    // a própria página de destino cuida de rolar/focar até a âncora certa.
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (location.hash) return;
    const heading = mainRef.current && mainRef.current.querySelector("h1, h2");
    const focusTarget = heading || mainRef.current;
    if (focusTarget) {
      if (!focusTarget.hasAttribute("tabindex")) focusTarget.setAttribute("tabindex", "-1");
      focusTarget.focus({ preventScroll: true });
    }
  }, [location.pathname]);

  return (
    <div className="institutional-app">
      <div className="topbar">
        <NavLink className="brand" to="/" aria-label="Mosaico — início">
          <img className="brand-logo" src="/institucional/logo-mosaico-wordmark.png" alt="Igreja Batista Mosaico — uma igreja, uma família, um propósito" />
        </NavLink>
        <nav className="toplinks" aria-label="Navegação principal">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} className={navClass} to={item.to} end={item.end}>
              {item.label}
            </NavLink>
          ))}
          {/* /financeiro é um site estático separado (public/financeiro/index.html),
              fora do roteamento do React Router -- precisa ser um <a> de
              verdade (navegação de página inteira), não um NavLink. Aparece
              pra qualquer pessoa logada (ver comentário de canSeeFinanceiro
              acima). */}
          {canSeeFinanceiro && <a className="navlink" href="/financeiro">Financeiro</a>}
          {canSeeEscala && <NavLink className={navClass} to="/admin/escalas">Escala</NavLink>}
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {viewerUser ? (
            <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
              <div style={{ minWidth: 0, display: "none" }} className="welcome-text">
                <p style={{ margin: 0, fontSize: 10.5, color: "#b8c0cd", textTransform: "uppercase", letterSpacing: 0.8 }}>Bem-vindo,</p>
                <p style={{ margin: 0, fontSize: 13, color: "#f7f9fe", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160 }}>{viewerUser.name}</p>
              </div>
              {/* Avatar sozinho, sem o pill "Perfil" ao lado -- clicável, leva pro
                  perfil dentro do Família Mosaico (pedido: ficou "estranho" ter
                  os dois juntos, iniciais bastam).
                  ?tab=perfil abre direto na aba Perfil (ver getInitialActiveTabFromUrl
                  em PrototypeApp.js) -- sem isso, clicar no próprio avatar caía no
                  feed "Hoje", quebrando a expectativa de que avatar = minha conta. */}
              <NavLink
                to="/familia?tab=perfil"
                aria-label={`Perfil de ${viewerUser.name || "usuário"}`}
                title="Perfil"
                style={{ width: 36, height: 36, borderRadius: 18, background: "#26282d", border: "1px solid rgba(245,210,139,0.28)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", fontSize: 12, color: "#f3d7a8", fontWeight: 900, flexShrink: 0, textDecoration: "none" }}
              >
                {viewerUser.profilePhotoUrl ? (
                  <img src={viewerUser.profilePhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                ) : (
                  getInitials(viewerUser.name)
                )}
              </NavLink>
            </div>
          ) : (
            <NavLink className="cta-pill" to="/entrar?next=%2Ffamilia">Entrar</NavLink>
          )}
        </div>
      </div>

      <main ref={mainRef}>
        <Outlet context={{ viewerUser }} />
      </main>

      <footer>
        <nav className="footer-links" aria-label="Links do rodapé">
          <NavLink to="/" end>Início</NavLink>
          <NavLink to="/sobre">Sobre</NavLink>
          <NavLink to="/ministerios">Ministérios</NavLink>
          <NavLink to="/pregacoes">Vídeos</NavLink>
          <NavLink to="/eventos">Eventos</NavLink>
          <NavLink to="/familia">Família Mosaico</NavLink>
          <NavLink to="/contato">Contato</NavLink>
          <NavLink to="/perguntas-frequentes">Perguntas frequentes</NavLink>
          <NavLink to="/devocionais">Devocionais e ebooks</NavLink>
          {canSeeFinanceiro && <a href="/financeiro">Financeiro</a>}
          {canSeeEscala && <NavLink to="/admin/escalas">Escala</NavLink>}
        </nav>
        Igreja Batista Mosaico · Av. dos Autonomistas, 2233 — Osasco, SP
      </footer>

      <nav className="tabbar" aria-label="Navegação">
        {TAB_ITEMS.map((item) => (
          <NavLink key={item.to} className={tabClass} to={item.to} end={item.end}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
